// 📂 frontend/src/services/emailApi.ts - VERSIUNEA FINALĂ COMPLETĂ
const API_URL = process.env.REACT_APP_API_URL || '/api';

interface EmailData {
  to: string;
  subject: string;
  content: string;
  cc?: string;
  bcc?: string;
  signature?: string;
  scheduled_for?: string;
  is_recurring?: boolean;
  recurring_type?: string;
  reminder_days?: number;
  priority?: string;
  email_type?: string;
}

interface EmailResponse {
  success: boolean;
  message: string;
  data?: Record<string, unknown>;
  error?: string;
}

interface Backup {
  filename: string;
  size: number;
  created: string;
  age_days: number;
}

interface BackupSettings {
  enabled: boolean;
  retention_days: number;
  backup_hour: number;
  backup_dir: string;
}

class EmailApiService {
  // Helper pentru request-uri cu credentials și error handling îmbunătățit
  private async fetchWithCredentials(url: string, options: RequestInit = {}): Promise<Response> {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    try {
      const response = await fetch(fullUrl, {
        cache: 'no-store',
        ...options,
        credentials: 'include', // Important pentru session cookies
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // Handle 401 specifically
      if (response.status === 401) {
        // Emit a custom event for global handling
        window.dispatchEvent(new CustomEvent('auth-error', { 
          detail: { status: 401, message: 'Session expired' } 
        }));
        
        // Clear local storage
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        
        throw new Error('Unauthorized: Session expired. Please login again.');
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return response;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Helper pentru parsing JSON cu error handling
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async parseJsonResponse(response: Response): Promise<any> {
    const text = await response.text();

    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<Record<string, unknown>> {
    try {
      
      const response = await this.fetchWithCredentials('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la autentificare');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  async logout(): Promise<Record<string, unknown>> {
    try {
      const response = await this.fetchWithCredentials('/auth/logout', {
        method: 'POST',
      });

      const data = await this.parseJsonResponse(response);
      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<Record<string, unknown>> {
    try {
      const response = await this.fetchWithCredentials('/auth/me');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Utilizator neautentificat');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Verifică sesiunea
  async checkSession(): Promise<boolean> {
    try {
      const response = await this.fetchWithCredentials('/auth/check-session');
      const data = await this.parseJsonResponse(response);
      return data.data?.valid || false;
    } catch (error) {
      console.error('Session check failed:', error);
      return false;
    }
  }

  // Trimite un email
  async sendEmail(emailData: EmailData): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials('/email/send', {
        method: 'POST',
        body: JSON.stringify(emailData),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la trimiterea email-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Programează un email - VERSIUNEA FIXATĂ
  async scheduleEmail(emailData: EmailData & { scheduled_for: string }): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials('/email/schedule', {
        method: 'POST',
        body: JSON.stringify(emailData),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la programarea email-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  async updateEmail(emailId: number, emailData: Record<string, unknown>): Promise<EmailResponse> {
    try {
      const response = await this.fetchWithCredentials(`/email/update/${emailId}`, {
        method: "PUT",
        body: JSON.stringify(emailData),
      });
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || "Eroare la actualizarea email-ului");
      }
      return data;
    } catch (error: unknown) {
      console.error("API error:", error);
      throw error;
    }
  }

  // Obține email-urile programate - VERSIUNEA ÎMBUNĂTĂȚITĂ
  async getScheduledEmails(): Promise<Record<string, unknown>[]> {
    try {
      
      const response = await this.fetchWithCredentials('/email/scheduled');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea email-urilor programate');
      }

      return data.data || [];
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Anulează un email programat
  async cancelScheduledEmail(emailId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/email/cancel/${emailId}`, {
        method: 'DELETE',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la anularea email-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Șterge permanent un email (atât programat cât și trimis) - METODĂ NOUĂ
  async deleteEmailPermanently(emailId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/email/delete/${emailId}`, {
        method: 'DELETE',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la ștergerea email-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Trimite email-uri în batch
  async sendBatchEmails(emails: EmailData[]): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials('/email/send-bulk', {
        method: 'POST',
        body: JSON.stringify({ recipients: emails.map(e => e.to), ...emails[0] }),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la trimiterea email-urilor');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Trimite email de test
  async sendTestEmail(recipientEmail: string): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/email/test/${recipientEmail}`, {
        method: 'GET',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la trimiterea email-ului de test');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Verifică conexiunea SMTP
  async verifyConnection(): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials('/email/verify');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la verificarea conexiunii');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Obține statistici
  async getStats(): Promise<{
    emailsSentToday: number;
    dailyLimit: number;
    remainingToday: number;
    lastReset: string;
    totalSent?: number;
    totalScheduled?: number;
  }> {
    try {
      
      const response = await this.fetchWithCredentials('/email/stats');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea statisticilor');
      }

      return data.data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Obține template-uri
  async getTemplates(): Promise<Record<string, unknown>[]> {
    try {
      
      const response = await this.fetchWithCredentials('/templates');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea template-urilor');
      }

      return data.data || [];
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Creează template nou
  async createTemplate(templateData: Record<string, unknown>): Promise<EmailResponse> {
    try {
      
      // Asigură-te că trimitem datele în formatul corect pentru backend
      const dataToSend = {
        name: templateData.name,
        subject: templateData.subject,
        content: templateData.content, // backend așteaptă 'content'
        body: templateData.content,    // trimitem și ca 'body' pentru compatibilitate
        category: templateData.category,
        tags: Array.isArray(templateData.tags) ? templateData.tags.join(',') : templateData.tags,
        is_public: templateData.isPublic,
        variables: JSON.stringify(templateData.variables || [])
      };
      
      
      const response = await this.fetchWithCredentials('/templates', {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la crearea template-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Obține un template specific
  async getTemplate(templateId: number): Promise<Record<string, unknown>> {
    try {
      
      const response = await this.fetchWithCredentials(`/templates/${templateId}`);
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea template-ului');
      }

      return data.data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Actualizează un template
  async updateTemplate(templateId: number, templateData: Record<string, unknown>): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify(templateData),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la actualizarea template-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Șterge un template
  async deleteTemplate(templateId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/templates/${templateId}`, {
        method: 'DELETE',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la ștergerea template-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Folosește un template
  async useTemplate(templateId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/templates/${templateId}/use`, {
        method: 'POST',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la folosirea template-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Duplică un template
  async duplicateTemplate(templateId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/templates/${templateId}/duplicate`, {
        method: 'POST',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la duplicarea template-ului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Export template-uri
  async exportTemplates(): Promise<void> {
    try {
      
      const response = await this.fetchWithCredentials('/templates/export');
      const blob = await response.blob();
      
      // Creează link de download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Import template-uri
  async importTemplates(file: File): Promise<EmailResponse> {
    try {
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/templates/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // Nu setăm Content-Type pentru FormData - browserul o va seta automat
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la importarea template-urilor');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Obține utilizatori (admin)
  async getUsers(): Promise<Record<string, unknown>[]> {
    try {
      
      const response = await this.fetchWithCredentials('/users');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea utilizatorilor');
      }

      return data.data || [];
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Creează utilizator nou (admin)
  async createUser(userData: Record<string, unknown>): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials('/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la crearea utilizatorului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Actualizează utilizator (admin)
  async updateUser(userId: number, userData: Record<string, unknown>): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la actualizarea utilizatorului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Șterge utilizator (admin)
  async deleteUser(userId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/users/${userId}`, {
        method: 'DELETE',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la ștergerea utilizatorului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Activează/Dezactivează utilizator (admin)
  async toggleUserStatus(userId: number): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/users/${userId}/toggle`, {
        method: 'PUT',
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la schimbarea statusului');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Resetează parola utilizatorului (admin)
  async resetUserPassword(userId: number, newPassword: string): Promise<EmailResponse> {
    try {
      
      const response = await this.fetchWithCredentials(`/users/${userId}/reset-password`, {
        method: 'PUT',
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la resetarea parolei');
      }

      return data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Obține statistici utilizatori (admin)
  async getUsersStats(): Promise<Record<string, unknown>> {
    try {
      
      const response = await this.fetchWithCredentials('/users/stats');
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Eroare la obținerea statisticilor');
      }

      return data.data;
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Export utilizatori CSV (admin)
  async exportUsers(): Promise<void> {
    try {
      
      const response = await this.fetchWithCredentials('/users/export');
      const blob = await response.blob();
      
      // Creează link de download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Verifică dacă API-ul este disponibil
  async checkHealth(): Promise<boolean> {
    try {
      
      const response = await this.fetchWithCredentials('/health');
      const data = await this.parseJsonResponse(response);
      
      const isHealthy = data.status === 'OK';
      
      return isHealthy;
    } catch (error) {
      console.error('API error:', error);
      return false;
    }
  }

  // Reîncarcă toate datele - FUNCȚIE NOUĂ PENTRU SINCRONIZARE
  async refreshAllData(): Promise<{
    emails: Record<string, unknown>[];
    templates: Record<string, unknown>[];
    users: Record<string, unknown>[];
    stats: Record<string, unknown>;
  }> {
    try {
      
      const [emails, templates, stats] = await Promise.all([
        this.getScheduledEmails().catch(() => []),
        this.getTemplates().catch(() => []),
        this.getStats().catch(() => ({}))
      ]);

      // Users doar pentru admin - va eșua pentru useri normali, dar nu ne pasă
      const users = await this.getUsers().catch(() => []);

      
      return { emails, templates, users, stats };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  // Backup operations
  async getBackups(): Promise<Backup[]> {
    try {
      const response = await this.fetchWithCredentials('/backup/list');
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la încărcarea backup-urilor');
      }
      return data.data || [];
    } catch (error: unknown) {
      console.error('Get backups error:', error);
      throw error;
    }
  }

  async getBackupSettings(): Promise<BackupSettings> {
    try {
      const response = await this.fetchWithCredentials('/backup/settings');
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la încărcarea setărilor');
      }
      return data.data;
    } catch (error: unknown) {
      console.error('Get backup settings error:', error);
      throw error;
    }
  }

  async createBackup(): Promise<EmailResponse> {
    try {
      const response = await this.fetchWithCredentials('/backup/create', {
        method: 'POST',
      });
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la crearea backup-ului');
      }
      return data;
    } catch (error: unknown) {
      console.error('Create backup error:', error);
      throw error;
    }
  }

  getBackupDownloadUrl(filename: string): string {
    return `${API_URL}/backup/download/${filename}`;
  }

  async restoreBackup(filename: string): Promise<EmailResponse> {
    try {
      const response = await this.fetchWithCredentials(`/backup/restore/${filename}`, {
        method: 'POST',
      });
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la restaurarea backup-ului');
      }
      return data;
    } catch (error: unknown) {
      console.error('Restore backup error:', error);
      throw error;
    }
  }

  async deleteBackup(filename: string): Promise<EmailResponse> {
    try {
      const response = await this.fetchWithCredentials(`/backup/delete/${filename}`, {
        method: 'DELETE',
      });
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la ștergerea backup-ului');
      }
      return data;
    } catch (error: unknown) {
      console.error('Delete backup error:', error);
      throw error;
    }
  }

  async saveBackupSettings(settings: BackupSettings): Promise<EmailResponse> {
    try {
      const response = await this.fetchWithCredentials('/backup/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      const data = await this.parseJsonResponse(response);
      if (!data.success) {
        throw new Error(data.error || 'Eroare la salvarea setărilor');
      }
      return data;
    } catch (error: unknown) {
      console.error('Save backup settings error:', error);
      throw error;
    }
  }

  // Notifications
  async getNotifications(): Promise<{data: Record<string, unknown>[], unread_count: number}> {
    const response = await this.fetchWithCredentials('/notifications');
    return this.parseJsonResponse(response);
  }
  async markNotificationsRead(): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/notifications/read', { method: 'POST' });
    return this.parseJsonResponse(response);
  }
  async clearNotifications(): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/notifications/clear', { method: 'POST' });
    return this.parseJsonResponse(response);
  }

  // Calendar
  async getCalendarData(year: number, month: number): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials(`/calendar?year=${year}&month=${month}`);
    return this.parseJsonResponse(response);
  }

  // Stats
  async getDetailedStats(days: number = 30): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials(`/stats/detailed?days=${days}`);
    return this.parseJsonResponse(response);
  }
  async getChartData(days: number = 7): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials(`/stats/chart?days=${days}`);
    return this.parseJsonResponse(response);
  }

  // Email extras
  async duplicateEmail(emailId: number): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials(`/email/${emailId}/duplicate`, { method: 'POST' });
    return this.parseJsonResponse(response);
  }
  async checkConflicts(to: string, subject: string): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/email/check-conflicts', {
      method: 'POST', body: JSON.stringify({ to, subject })
    });
    return this.parseJsonResponse(response);
  }
  async bulkEditEmails(emailIds: number[], updates: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/email/bulk-edit', {
      method: 'POST', body: JSON.stringify({ email_ids: emailIds, updates })
    });
    return this.parseJsonResponse(response);
  }

  // Password
  async changePassword(oldPassword: string, newPassword: string): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/auth/change-password', {
      method: 'POST', body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
    });
    return this.parseJsonResponse(response);
  }

  // Logs
  async getLogs(lines: number = 100, level: string = 'ALL', search: string = ''): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials(`/logs?lines=${lines}&level=${level}&search=${encodeURIComponent(search)}`);
    return this.parseJsonResponse(response);
  }
  async clearLogs(): Promise<Record<string, unknown>> {
    const response = await this.fetchWithCredentials('/logs/clear', { method: 'POST' });
    return this.parseJsonResponse(response);
  }

  // Contact Groups
  async getGroups(): Promise<Record<string, unknown>[]> {
    const resp = await this.fetchWithCredentials('/groups');
    const data = await this.parseJsonResponse(resp);
    return data.data || [];
  }
  async createGroup(name: string, color: string): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials('/groups', { method: 'POST', body: JSON.stringify({ name, color }) });
    return this.parseJsonResponse(resp);
  }
  async deleteGroup(groupId: number): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials(`/groups/${groupId}`, { method: 'DELETE' });
    return this.parseJsonResponse(resp);
  }
  async getGroupEmails(groupId: number): Promise<string[]> {
    const resp = await this.fetchWithCredentials(`/groups/${groupId}/emails`);
    const data = await this.parseJsonResponse(resp);
    return data.data || [];
  }

  // Contacts
  async getContacts(search: string = ''): Promise<Record<string, unknown>[]> {
    const resp = await this.fetchWithCredentials(`/contacts?search=${encodeURIComponent(search)}`);
    const data = await this.parseJsonResponse(resp);
    return data.data || [];
  }
  async createContact(contact: Record<string, string>): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials('/contacts', { method: 'POST', body: JSON.stringify(contact) });
    return this.parseJsonResponse(resp);
  }
  async updateContact(id: number, contact: Record<string, string>): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(contact) });
    return this.parseJsonResponse(resp);
  }
  async deleteContact(id: number): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials(`/contacts/${id}`, { method: 'DELETE' });
    return this.parseJsonResponse(resp);
  }
  async importContacts(contacts: Record<string, string>[]): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials('/contacts/import', { method: 'POST', body: JSON.stringify({ contacts }) });
    return this.parseJsonResponse(resp);
  }

  // Retry email
  async retryEmail(emailId: number): Promise<Record<string, unknown>> {
    const resp = await this.fetchWithCredentials('/email/bulk-edit', {
      method: 'POST',
      body: JSON.stringify({ email_ids: [emailId], updates: { status: 'scheduled' } })
    });
    return this.parseJsonResponse(resp);
  }

  // Export emails CSV
  getExportEmailsCsvUrl(): string {
    return `${API_URL}/export/emails`;
  }

  // Timezones
  async getTimezones(): Promise<string[]> {
    const resp = await this.fetchWithCredentials('/timezones');
    const data = await this.parseJsonResponse(resp);
    return data.data || [];
  }

  // File upload
  async uploadFile(file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    const API = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${API}/upload`, {
      method: 'POST', body: formData, credentials: 'include'
    });
    return response.json();
  }

  // Force sync pentru debugging
  async forceSync(): Promise<EmailResponse['data'][]> {
    try {
      
      // Forțează o cerere fresh fără cache
      const timestamp = Date.now();
      const response = await this.fetchWithCredentials(`/email/scheduled?_t=${timestamp}`);
      const data = await this.parseJsonResponse(response);

      if (!data.success) {
        console.error('❌ Force sync failed:', data.error);
        return [];
      }

      return data.data || [];
    } catch (error) {
      console.error('API error:', error);
      return [];
    }
  }
}

// Export singleton instance
const emailApiInstance = new EmailApiService();
export default emailApiInstance;